/**
 * Multi-Agent SmartBot Widget - Floating AI Assistant Button
 * 
 * This component provides a floating action button that opens the multi-agent AI chat bot.
 * The multi-agent system includes specialized agents for job information, payroll, and more.
 */

import React, { useState } from "react";
import { Bot } from "lucide-react";
import { MultiAgentSmartBot } from "./MultiAgentSmartBot";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureEnabled } from "@/contexts/FeatureFlagContext";
import { useChatWidgetStore } from "@/stores/useChatWidgetStore";

interface MultiAgentSmartBotWidgetProps {
  // Optional: Control when the bot should be shown
  showOnPages?: string[]; // e.g., ["/dashboard", "/employees"]
  hideOnPages?: string[]; // e.g., ["/login", "/signup"]
  
  // Optional: Employee context (useful when on employee detail pages)
  employeeId?: number;
  employeeName?: string;
}

export const MultiAgentSmartBotWidget: React.FC<MultiAgentSmartBotWidgetProps> = ({
  showOnPages,
  hideOnPages,
  employeeId,
  employeeName,
}) => {
  const { isOpen, openChat, closeChat } = useChatWidgetStore();
  const location = useLocation();
  const { user } = useAuth();
  const isMultiAgentBotEnabled = useFeatureEnabled('multi_agent_bot');

  // Determine if bot should be visible on current page
  const shouldShowBot = () => {
    // First check if the feature is enabled
    if (!isMultiAgentBotEnabled) {
      return false;
    }

    const currentPath = location.pathname;

    // Hide on specific pages (exact match or starts with)
    if (hideOnPages) {
      for (const page of hideOnPages) {
        if (currentPath === page || (page !== "/" && currentPath.startsWith(page))) {
          return false;
        }
      }
    }

    // Show only on specific pages
    if (showOnPages && !showOnPages.some(page => currentPath.startsWith(page))) {
      return false;
    }

    return true;
  };

  if (!shouldShowBot()) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          onClick={() => openChat()}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-14 h-14 bg-primary hover:bg-primary/90 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 group"
          aria-label="Open AI Assistant"
        >
          <Bot className="h-6 w-6" />
          
          {/* Tooltip */}
          <span className="absolute right-16 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Ask AI Assistant
          </span>
          
          {/* Pulse animation */}
          <span className="absolute inset-0 rounded-full bg-primary animate-ping opacity-20"></span>
        </button>
      )}

      {/* Multi-Agent Smart Bot Chat Window */}
      {isOpen && (
        <MultiAgentSmartBot
          currentPage={location.pathname}
          employeeId={employeeId}
          employeeName={employeeName}
          onClose={closeChat}
        />
      )}
    </>
  );
};

export default MultiAgentSmartBotWidget;
